import { useMemo } from 'react';
import { useReportingExcludeDates } from 'hooks/useFeatureFlag';
import { lowerCase, startCase } from 'lodash';
import { useAppSelector } from 'slices/store';
import { timeZonesSelector } from 'slices/timeZone.slice';
import { EReportTemplateType, EReportingTimeType } from '@timeedit/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { DateRangePicker } from '../components/DateRangePicker';
import { ExcludedDates } from '../components/ExcludedDates';
import { ExcludedTimes } from '../components/ExcludedTimes';
import TimeRangePicker from '../components/TimeRangePicker';
import type { TReportTemplateProps } from './ReportTemplate';
import WeekdaySettings from './WeekdaySettings';

const availableTypeOptions = Object.keys(EReportingTimeType).map((key) => ({
  value: key,
  label: startCase(lowerCase(key)),
}));

const TimeSetting = ({ data, onChange }: TReportTemplateProps) => {
  const timezones = useAppSelector(timeZonesSelector);
  const timezoneOptions = useMemo(
    () =>
      timezones
        .filter((tz) => !!tz.identifiers?.length)
        .map((tz) => ({
          value: tz.identifiers[0],
          label: tz.name,
        })),
    [timezones]
  );

  const filteredAvailableTypeOptions = useMemo(
    () =>
      availableTypeOptions.filter((option) => {
        if (data.templateType === EReportTemplateType.DEMAND_ANALYTIC) {
          return option.value !== EReportingTimeType.MAX_HOURS;
        }
        return true;
      }),
    [data]
  );

  const enableExcudeDates = useReportingExcludeDates();

  return (
    <TEDrawerSection label="Time Setting">
      <div className="section-wrapper">
        <div className="section-col">
          <TEInput.Select
            label="Available type"
            tooltip="Available type"
            value={data.availableType}
            onChange={(val) => onChange('availableType', val)}
            options={filteredAvailableTypeOptions}
            data-testid="available-type"
          />
          <TEInput.Select
            label="Timezone"
            tooltip="Timezone should match the timezone of the reservation in TE Core"
            value={data.timezone}
            onChange={(val) => onChange('timezone', val)}
            options={timezoneOptions}
            data-testid="timezone"
          />
          <TEInput.LabelWrapper label="Date Range" tooltip="Date Range">
            <DateRangePicker
              value={{
                dateStart: data.dateRangeStart,
                dateEnd: data.dateRangeEnd,
              }}
              onChange={(dateStrings) => {
                onChange('dateRangeStart', dateStrings.dateStart);
                onChange('dateRangeEnd', dateStrings.dateEnd);
              }}
              timezone={data.timezone}
            />
          </TEInput.LabelWrapper>
          {enableExcudeDates && (
            <ExcludedDates
              excludedDates={data.excludedDates}
              onChange={(excludedDates) =>
                onChange('excludedDates', excludedDates)
              }
              timezone={data.timezone}
            />
          )}
          {data.availableType === EReportingTimeType.TIME_RANGE && (
            <>
              <TimeRangePicker
                label="Default time range"
                tooltip="Time ranged used for days not having customized time ranges configured"
                value={{
                  startTime: data.availableStartTime,
                  endTime: data.availableEndTime,
                }}
                onChange={(val) => {
                  onChange('availableStartTime', val.startTime);
                  onChange('availableEndTime', val.endTime);
                }}
              />
              {enableExcudeDates && (
                <ExcludedTimes
                  excludedTimes={data.defaultExcludedTimes}
                  onChange={(excludedTimes) =>
                    onChange('defaultExcludedTimes', excludedTimes)
                  }
                />
              )}
            </>
          )}
          {data.availableType === EReportingTimeType.FIXED_HOUR && (
            <TEInput.InputNumber
              label="Available hour"
              tooltip="Available hour"
              value={data.availableHour}
              onChange={(val) => onChange('availableHour', val)}
              data-testid="available-hour"
            />
          )}
          <WeekdaySettings
            reportTemplate={data}
            onChange={(value) => onChange('weekdaySettings', value)}
          />
        </div>
      </div>
    </TEDrawerSection>
  );
};

export default TimeSetting;
